import { makeStyles, styled } from "@material-ui/styles";
import { useCollection, useIO, useLinked, useModel } from "@type-r/react";
import { Button, Column, Columns, Flex, Loading, SearchInput, div } from "lqx-react";
import React, { useEffect, useMemo, useState } from "react";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { Can } from "src/app/services/can";
import { syncAssetEventsToAssets } from "../api/assetChange";
import { AssetEvent, AssetEventStatus, AssetEventType, GenericExternalSystemType, getAssetEvents, pollAssetEvents, fetchEventsBase } from "../api/assetEvent";
import { ExternalSystem, ExternalSystemRequest } from "../api/externalSystemRequest";
import { getFacilities } from "../api/facilities";
import { getPrograms } from "../api/programs";
import { DigitizationSelect } from "../components/select";
import { MultiSelectDropdown } from "../connections/components/multiselect-dropdown";
import { BackButton, WorkflowBody, WorkflowColumn, WorkflowFooter, WorkflowTitle } from "../shared";
import { ArchivedAssets } from "../api/archivedAsset";
import { Snackbar } from "@material-ui/core";
import { Alert, AlertTitle, Pagination } from "@material-ui/lab";
import { AssetEventGeneralCount } from "../api/assetEventGeneralCount";
import { LatestAssetEvent } from "../api/latestAssetEvent";
import { DigitizedAssetMasterCount } from "../api/digitizedAssetMasterCount";
import { PlatformProducts } from "../api/platformProducts";
import { getPlatformCode } from "../api/endpoint";


export const InblockedAssetsDataStep = (
    {
        assetName,
        assetChanges,
        selectedAssets,
        assetsListComponent,
        externalSystems,
        job,
        back,
        $assetTypeSelect,
        $filter,
        $loadedAssetEvents,
        assetTypeMultiOptions,
        $refreshEvents,
        selectAllAssets
    }:
        {
            assetName: string,
            assetChanges: any,
            selectedAssets: any,
            assetsListComponent,
            externalSystems: ExternalSystem[],
            job?,
            back?,
            $assetTypeSelect?,
            $filter?,
            $loadedAssetEvents,
            assetTypeMultiOptions?,
            $refreshEvents?,
            selectAllAssets?
        }) => {

    const [selectedFacilities, setSelectedFacilities] = React.useState<any>();
    const [selectedTargetProgram, setSelectedTargetProgram] = React.useState<any>();

    const $showErrors = useLinked(false);
    const $errorMessage = useLinked('');

    const $currentPage = useLinked(1);
    const activePollers = React.useRef<Map<number, { startTime: number, importJobId?: number, assetUids?: string[], timeoutId?: any }>>(new Map());

    const handleCloseShowErrors = (_, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        $showErrors.set(false);
    };

    const getAllAssetEvents = async () => {
        if (assetChanges.items.length > 0) {
            //console.log("getAllAssetEvents with assetChanges.length:" + assetChanges.items.length);
            await getAssetEvents(assetChanges.items, incomingAssetEvents, assetChanges.items.length);
        }
    };

    const selfPollAssetEvents = async () => {
        //console.log("selfPollAssetEvents with selectedAssets.length:" + selectedAssets.items.length);
        await pollAssetEvents(selectedAssets.items, incomingAssetEvents, assetChanges.items.length);
    };

    const selfSyncAssetEventsToAssets = (eventTypeToSync?: number) => {
        if (eventTypeToSync) {
            const eventsToSync = incomingAssetEvents.models.filter(e => e.eventTypeId === eventTypeToSync);
            const filteredCollection = {
                models: eventsToSync,
                length: eventsToSync.length,
                forEach: function(callback) {
                    eventsToSync.forEach(callback);
                },
                filter: function(callback) {
                    return eventsToSync.filter(callback);
                },
                transaction: incomingAssetEvents.transaction.bind(incomingAssetEvents),
                add: incomingAssetEvents.add.bind(incomingAssetEvents),
                set: incomingAssetEvents.set.bind(incomingAssetEvents),
                reset: incomingAssetEvents.reset.bind(incomingAssetEvents)
            };
            const externalSystemForEventType = externalSystems.find(es => es.assetEventType === eventTypeToSync);
            if (externalSystemForEventType) {
                syncAssetEventsToAssets(assetChanges.items, filteredCollection, [externalSystemForEventType], $refreshEvents);
            }
        } else {
            syncAssetEventsToAssets(assetChanges.items, incomingAssetEvents, externalSystems, $refreshEvents);
        }
    };

    const getItems = React.useCallback(async (searchText) => {
        return (await getFacilities(searchText));
    }, []);

    const getProgramItems = React.useCallback(async (searchText) => {
        return (await getPrograms(searchText));
    }, []);

    const totalCountsByType = (data) =>
        data.reduce((acc, item) => {
            if (!acc[item.eventTypeId]) {
                acc[item.eventTypeId] = 0;
            }
            acc[item.eventTypeId] += item.count;
            return acc;
        }, {});

    const checkCount = (data, generalCount) => {
        for (const [assetEventTypeId, total] of Object.entries(totalCountsByType(data))) {
            if (total === generalCount) {
                return false;
            }
        }
        return true;
    }

    const pollForEventType = async (eventTypeId: number) => {
        const pollerInfo = activePollers.current.get(eventTypeId);
        if (!pollerInfo) {
            return;
        }

        const { startTime, importJobId, assetUids } = pollerInfo;
        const pollStartTime = startTime;
        
        const existingEventsForOtherTypes = incomingAssetEvents.models.filter(e => e.eventTypeId !== eventTypeId);
        const existingEventsForThisType = incomingAssetEvents.models.filter(e => e.eventTypeId === eventTypeId);
        
        let assetsToPoll;
        if (importJobId) {
            const filteredAssets = assetChanges.items.models.filter(asset => asset.filter($filter.value));
            assetsToPoll = { models: filteredAssets, length: filteredAssets.length };
        } else if (assetUids && assetUids.length > 0) {
            const filteredAssets = assetChanges.items.models.filter(asset => assetUids.includes(asset.assetId));
            assetsToPoll = { models: filteredAssets, length: filteredAssets.length };
        } else {
            const filteredAssets = assetChanges.items.models.filter(asset => selectedAssets.items.models.some(selected => selected.assetId === asset.assetId));
            assetsToPoll = { models: filteredAssets, length: filteredAssets.length };
        }
        
        const assetsToCount = assetsToPoll.models;
        
        if (importJobId) {
            const totalAssetCount = assetChanges.count || assetChanges.items.length;
            
            await assetEventsGeneralCount.fetch({ params: { importJobId: importJobId } }).catch(e => console.error(e));
            
            const countsForEventType = assetEventsGeneralCount.models.filter(ge => 
                ge.eventTypeId === eventTypeId
            );
            
            const successCount = countsForEventType.filter(ge => ge.success).reduce((sum, ge) => sum + ge.count, 0);
            const failureCount = countsForEventType.filter(ge => !ge.success).reduce((sum, ge) => sum + ge.count, 0);
            
            const tempCollection = { models: [], set: (events) => { 
                if (Array.isArray(events)) {
                    tempCollection.models = events;
                } else if (events && typeof events.forEach === 'function') {
                    tempCollection.models = Array.from(events);
                } else {
                    tempCollection.models = events ? [events] : [];
                }
            }, getEndpoint: () => incomingAssetEvents.getEndpoint() };
            await getAssetEvents(assetChanges.items, tempCollection, totalAssetCount, importJobId, true).catch(e => console.error(e));
            
            const fetchedEvents = Array.isArray(tempCollection.models) ? tempCollection.models : [];
            const newEventsForThisType = fetchedEvents.filter(e => e && e.eventTypeId === eventTypeId);
            
            const existingEventsForOtherTypes = incomingAssetEvents.models.filter(e => e.eventTypeId !== eventTypeId);
            const existingEventsForThisType = incomingAssetEvents.models.filter(e => e.eventTypeId === eventTypeId);
            
            incomingAssetEvents.transaction(() => {
                for (const newEvent of newEventsForThisType) {
                    if (newEvent.id) {
                        const existingEvent = existingEventsForThisType.find(e => e.id === newEvent.id);
                        if (existingEvent) {
                            existingEvent.set({ ...newEvent.attributes, status: AssetEventStatus.received });
                        } else {
                            incomingAssetEvents.add({ ...newEvent, status: AssetEventStatus.received });
                        }
                    }
                }
            });
            
            if ($refreshEvents) {
                $refreshEvents.set(!$refreshEvents.value);
            }
            
            selfSyncAssetEventsToAssets(eventTypeId);
            
            $loadedAssetEvents.set(true);
            if ($refreshEvents) {
                $refreshEvents.set(!$refreshEvents.value);
            }
        } else {
            const tempCollection = { models: [], set: (events) => { tempCollection.models = events; }, getEndpoint: () => incomingAssetEvents.getEndpoint() };
            await getAssetEvents(assetsToPoll, tempCollection, assetsToPoll.length, undefined, false).catch(e => console.error(e));
            
            const fetchedEvents = tempCollection.models || [];
            const newEventsForThisType = fetchedEvents.filter(e => e.eventTypeId === eventTypeId);
            const mergedEventsForThisType = [...existingEventsForThisType];
            for (const newEvent of newEventsForThisType) {
                const eventData = { ...newEvent };
                if (eventData.status === undefined || eventData.status === AssetEventStatus.pending) {
                    eventData.status = AssetEventStatus.received;
                }
                const existingIndex = mergedEventsForThisType.findIndex(e => e.id === eventData.id);
                if (existingIndex >= 0) {
                    mergedEventsForThisType[existingIndex] = eventData;
                } else {
                    mergedEventsForThisType.push(eventData);
                }
            }
            incomingAssetEvents.set([...existingEventsForOtherTypes, ...mergedEventsForThisType]);
            
            incomingAssetEvents.models.forEach(event => {
                if (event.eventTypeId === eventTypeId && (event.status === undefined || event.status === AssetEventStatus.pending)) {
                    event.set({ ...event.attributes, status: AssetEventStatus.received });
                }
            });
            
            if ($refreshEvents) {
                $refreshEvents.set(!$refreshEvents.value);
            }
            
            selfSyncAssetEventsToAssets(eventTypeId);
            
            if ($refreshEvents) {
                $refreshEvents.set(!$refreshEvents.value);
            }
            
            const assetsWithEvent = assetsToCount.filter(asset => {
                const event = asset.assetEvents.models.find(e => 
                    e.eventTypeId === eventTypeId &&
                    e.status === AssetEventStatus.received
                );
                return event !== undefined;
            });
            
            const assetsWithEventSuccess = assetsWithEvent.filter(asset => {
                const event = asset.assetEvents.models.find(e => 
                    e.eventTypeId === eventTypeId &&
                    e.status === AssetEventStatus.received
                );
                return event && event.success;
            });
            
            const assetsWithEventFailure = assetsWithEvent.filter(asset => {
                const event = asset.assetEvents.models.find(e => 
                    e.eventTypeId === eventTypeId &&
                    e.status === AssetEventStatus.received
                );
                return event && !event.success;
            });
            
            const successCount = assetsWithEventSuccess.length;
            const failureCount = assetsWithEventFailure.length;
            
            const existingForEventType = assetEventsGeneralCount.models.filter(ge => 
                ge.eventTypeId === eventTypeId
            );
            existingForEventType.forEach(existing => {
                assetEventsGeneralCount.remove(existing);
            });
            
            const syntheticGeneralCount = [];
            if (successCount > 0) {
                syntheticGeneralCount.push({
                    eventTypeId: eventTypeId,
                    count: successCount,
                    success: true,
                    importJobId: job.importJobId,
                    uid: null
                });
            }
            if (failureCount > 0) {
                syntheticGeneralCount.push({
                    eventTypeId: eventTypeId,
                    count: failureCount,
                    success: false,
                    importJobId: job.importJobId,
                    uid: null
                });
            }
            
            syntheticGeneralCount.forEach(synthetic => {
                assetEventsGeneralCount.add(synthetic);
            });
            
            $loadedAssetEvents.set(true);
            if ($refreshEvents) {
                $refreshEvents.set(!$refreshEvents.value);
            }
        }
        
        const assetCount = importJobId 
            ? assetChanges.count 
            : assetsToCount.length;
        const POLLING_TIMEOUT_MS = assetCount <= 100 ? 60000 : 120000;
        const elapsedTime = Date.now() - pollStartTime;
        const hasTimedOut = elapsedTime >= POLLING_TIMEOUT_MS;
        
        let allAssetsHaveFinalStatus = false;
        if (importJobId) {
            const allEventsForType = incomingAssetEvents.models.filter(e => e.eventTypeId === eventTypeId && e.id);
            const uniqueAssetIds = new Set<string>();
            for (const event of allEventsForType) {
                const assetId = event.uid || (event.assetId ? event.assetId.toString() : null);
                if (assetId) {
                    uniqueAssetIds.add(assetId);
                }
            }
            allAssetsHaveFinalStatus = uniqueAssetIds.size >= assetCount && assetCount > 0;
            
            if (!allAssetsHaveFinalStatus) {
                const countsForEventType = assetEventsGeneralCount.models.filter(ge => 
                    ge.eventTypeId === eventTypeId
                );
                const totalCountFromCounts = countsForEventType.reduce((sum, ge) => sum + ge.count, 0);
                if (totalCountFromCounts >= assetCount && assetCount > 0) {
                    allAssetsHaveFinalStatus = true;
                }
            }
        } else {
            const assetsWithFinalStatus = assetsToCount.filter(asset => {
                const event = asset.assetEvents.models.find(e => 
                    e.eventTypeId === eventTypeId &&
                    e.status === AssetEventStatus.received
                );
                return event !== undefined;
            });
            allAssetsHaveFinalStatus = assetsWithFinalStatus.length === assetsToCount.length && assetsToCount.length > 0;
        }
        
        if (hasTimedOut && !allAssetsHaveFinalStatus) {
            let totalSuccessCountAfterTimeout = 0;
            let totalFailureCountAfterTimeout = 0;
            
            if (importJobId) {
                assetChanges.items.models.forEach(asset => {
                    const dynamic: any = asset;
                    const pendingEvent = dynamic.assetEvents.models.find(e => 
                        e.eventTypeId === eventTypeId &&
                        e.status === AssetEventStatus.pending
                    );
                    if (pendingEvent) {
                        pendingEvent.set({
                            ...pendingEvent.attributes,
                            status: AssetEventStatus.received,
                            success: false,
                            id: pendingEvent.id || 'timeout-' + Date.now()
                        });
                    }
                });
                
                if ($refreshEvents) {
                    $refreshEvents.set(!$refreshEvents.value);
                }
                
                const eventsForEventType = incomingAssetEvents.models.filter(e => 
                    e.eventTypeId === eventTypeId &&
                    e.status === AssetEventStatus.received
                );
                
                const uniqueSuccessAssetIds = new Set<string>();
                const uniqueFailureAssetIds = new Set<string>();
                
                for (const event of eventsForEventType) {
                    if (event.uid) {
                        if (event.success) {
                            uniqueSuccessAssetIds.add(event.uid);
                        } else {
                            uniqueFailureAssetIds.add(event.uid);
                        }
                    }
                }
                
                const timedOutCount = assetCount - (uniqueSuccessAssetIds.size + uniqueFailureAssetIds.size);
                totalSuccessCountAfterTimeout = uniqueSuccessAssetIds.size;
                totalFailureCountAfterTimeout = uniqueFailureAssetIds.size + (timedOutCount > 0 ? timedOutCount : 0);
            } else {
                const assetsWithoutTargetEvent = assetsToCount.filter(asset => {
                    const event = asset.assetEvents.models.find(e => 
                        e.eventTypeId === eventTypeId &&
                        e.status === AssetEventStatus.received
                    );
                    return event === undefined;
                });
                
                if (assetsWithoutTargetEvent.length > 0) {
                    assetsWithoutTargetEvent.forEach(asset => {
                        const dynamic: any = asset;
                        const pendingEvent = dynamic.assetEvents.models.find(e => 
                            e.eventTypeId === eventTypeId &&
                            e.status === AssetEventStatus.pending
                        );
                        if (pendingEvent) {
                            pendingEvent.set({
                                ...pendingEvent.attributes,
                                status: AssetEventStatus.received,
                                success: false,
                                id: pendingEvent.id || 'timeout-' + Date.now()
                            });
                        }
                    });
                }
                
                const assetsWithTargetEventAfterTimeout = assetsToCount.filter(asset => {
                    const event = asset.assetEvents.models.find(e => 
                        e.eventTypeId === eventTypeId &&
                        e.status === AssetEventStatus.received
                    );
                    return event !== undefined;
                });
                
                const assetsWithTargetEventSuccessAfterTimeout = assetsWithTargetEventAfterTimeout.filter(asset => {
                    const event = asset.assetEvents.models.find(e => 
                        e.eventTypeId === eventTypeId &&
                        e.status === AssetEventStatus.received
                    );
                    return event && event.success;
                });
                
                const assetsWithTargetEventFailureAfterTimeout = assetsWithTargetEventAfterTimeout.filter(asset => {
                    const event = asset.assetEvents.models.find(e => 
                        e.eventTypeId === eventTypeId &&
                        e.status === AssetEventStatus.received
                    );
                    return event && !event.success;
                });
                
                totalSuccessCountAfterTimeout = assetsWithTargetEventSuccessAfterTimeout.length;
                totalFailureCountAfterTimeout = assetsWithTargetEventFailureAfterTimeout.length;
            }
            
            const existingForEventType = assetEventsGeneralCount.models.filter(ge => 
                ge.eventTypeId === eventTypeId
            );
            existingForEventType.forEach(existing => {
                assetEventsGeneralCount.remove(existing);
            });
            
            const syntheticGeneralCount = [];
            if (totalSuccessCountAfterTimeout > 0) {
                syntheticGeneralCount.push({
                    eventTypeId: eventTypeId,
                    count: totalSuccessCountAfterTimeout,
                    success: true,
                    importJobId: job.importJobId,
                    uid: null
                });
            }
            if (totalFailureCountAfterTimeout > 0) {
                syntheticGeneralCount.push({
                    eventTypeId: eventTypeId,
                    count: totalFailureCountAfterTimeout,
                    success: false,
                    importJobId: job.importJobId,
                    uid: null
                });
            }
            
            syntheticGeneralCount.forEach(synthetic => {
                assetEventsGeneralCount.add(synthetic);
            });
            
            if ($refreshEvents) {
                $refreshEvents.set(!$refreshEvents.value);
            }
        }
        
        if (allAssetsHaveFinalStatus || hasTimedOut) {
            activePollers.current.delete(eventTypeId);
            return;
        }
        
        if (!allAssetsHaveFinalStatus && !hasTimedOut) {
            const pollInterval = assetCount > 100 ? 20000 : 3000;
            const timeoutId = setTimeout(() => pollForEventType(eventTypeId), pollInterval);
            pollerInfo.timeoutId = timeoutId;
        }
    };

    const sendToExternalSystem = async (externalSystem: string, assetType: string, assetEventType: number, importJobId?: number) => {
        try {
            const selectedAssetsUids = selectedAssets.items.models.map(asset => asset.assetId);

            if (selectedAssetsUids.length <= 0 && importJobId === 0) return;

            $loadedAssetEvents.set(false);

            if (activePollers.current.has(assetEventType)) {
                const existingPoller = activePollers.current.get(assetEventType);
                if (existingPoller.timeoutId) {
                    clearTimeout(existingPoller.timeoutId);
                }
            }

            if (importJobId) {
                assetChanges.items.models.forEach(asset => asset.resetAssetEvents(assetEventType, incomingAssetEvents));
                assetChanges.items.models.forEach(asset => asset.requireAssetEvent(assetEventType, true));

                externalSystemRequest.set({
                    to: externalSystem,
                    assetType: assetType,
                    facilityIds: selectedFacilities ? selectedFacilities.map(f => f.id) : [],
                    programId: selectedTargetProgram ? selectedTargetProgram.id : null,
                    importJobId: importJobId
                });
            } else {
                selectedAssets.items.models.forEach(asset => asset.resetAssetEvents(assetEventType, incomingAssetEvents));
                selectedAssets.items.models.forEach(asset => asset.requireAssetEvent(assetEventType, true));

                externalSystemRequest.set({
                    to: externalSystem,
                    assetType: assetType,
                    assetUids: selectedAssetsUids,
                    facilityIds: selectedFacilities ? selectedFacilities.map(f => f.id) : [],
                    programId: selectedTargetProgram ? selectedTargetProgram.id : null,
                    importJobId: 0
                });
            }

            await externalSystemRequest.save().catch(err => {
                $showErrors.set(true);
                $errorMessage.set(err.message.replace(/:/g, ""));
                selectedAssets.items.models.forEach(asset => asset.resetAssetEvents(assetEventType, incomingAssetEvents));
                activePollers.current.delete(assetEventType);
            });

            const assetUidsToPoll = importJobId ? undefined : selectedAssetsUids;
            const pollStartTime = Date.now();
            
            activePollers.current.set(assetEventType, {
                startTime: pollStartTime,
                importJobId: importJobId,
                assetUids: assetUidsToPoll
            });

            const assetCount = importJobId ? assetChanges.count : selectedAssetsUids.length;
            const pollInterval = assetCount > 100 ? 20000 : 3000;
            const timeoutId = setTimeout(() => pollForEventType(assetEventType), pollInterval);
            const pollerInfo = activePollers.current.get(assetEventType);
            if (pollerInfo) {
                pollerInfo.timeoutId = timeoutId;
            }
        }
        finally {
            $sendToExternalSystem.set(null);
        }
    };

    const archivedAssets = useModel(ArchivedAssets);
    const externalSystemRequest = useModel(ExternalSystemRequest);
    const incomingAssetEvents = useCollection.of(AssetEvent);
    const assetEventsGeneralCount = useCollection.of(AssetEventGeneralCount);
    const latestAssetEvent = useCollection.of(LatestAssetEvent);
    const digitizedAssetMasterCount = useModel(DigitizedAssetMasterCount);
    const platformProducts = useModel(PlatformProducts);

    const $width = useLinked(450)
    const $sendToExternalSystem = useLinked<{ systemType: string, assetType: string, assetEventType: AssetEventType, importJobId?: number }>(null);

    const filteredAssetChanges = useMemo(() => assetChanges.items.models.filter(assetChange => { return assetChange.filter($filter.value); }), [assetChanges['_changeToken'], $filter['_changeToken']]);
    const [pollAssetCountAndDisableSendButton, setPollAssetCountAndDisableSendButton] = useState(true);
    const disableButtonsFlag = (selectedAssets && selectedAssets.items.some(item => item.isArchived)) || !assetChanges.count;

    useEffect(() => {
        let timeout;

        const fetchData = async () => {
            await digitizedAssetMasterCount.fetch({ params: { importJobId: job.importJobId } }).catch(e => console.error(e));

            if (assetChanges.count === digitizedAssetMasterCount.count || $assetTypeSelect /* ignore for multi asset type */) {
                setPollAssetCountAndDisableSendButton(false);
                return; // Stop polling when counts match
            }
            // Schedule the next call after 5 seconds
            timeout = setTimeout(fetchData, 5000);
        };

        fetchData(); // Initial call

        return () => clearTimeout(timeout); // Cleanup on unmount
    }, [assetChanges.count]); // Depend only on uiCount, so it updates if needed

    const isReadyPagination = useIO(async () => {
        await assetChanges.fetch({ params: { jobId: job.id, isDigitized: true, skip: ($currentPage.value - 1) * 100, take: 100 } }).catch(e => console.error(e));

        await archivedAssets.getArchivedAssets(job.importJobId).catch(e => console.error(e));
        //marked assets from digitization database as isArchived
        archivedAssets.items.models.map((item) => {
            let assetChange = assetChanges.items.models.find(asset => asset.assetId === item.uid);
            if (assetChange) {
                assetChange.isArchived = true;
            }
        });

        await platformProducts.getPlatformProducts(getPlatformCode()).catch(e => console.error(e));

        assetChanges.items.set(assetChanges.items.models.filter(asset => asset.isDigitized));
        await getAllAssetEvents();

        if (!selectAllAssets || !selectAllAssets.value || activePollers.current.size === 0) {
            await assetEventsGeneralCount.fetch({ params: { importJobId: job.importJobId } }).catch(e => console.error(e));
        }
        $loadedAssetEvents.set(true);

        await latestAssetEvent.fetch({ params: { importJobId: job.importJobId } }).catch(e => console.error(e));
    }, [$assetTypeSelect ? $assetTypeSelect['_changeToken'] : null, $currentPage.value]);

    useIO(async () => {
        if ($sendToExternalSystem.value) {
            // Capture values immediately to avoid closure issues
            const { systemType, assetType, assetEventType, importJobId } = $sendToExternalSystem.value;
            await sendToExternalSystem(systemType, assetType, assetEventType, importJobId);
        }
    }, [$sendToExternalSystem['_changeToken']]);

    useEffect(() => {
        if (!selectAllAssets || !selectAllAssets.value) {
            selfSyncAssetEventsToAssets();
        }
    }, [incomingAssetEvents['_changeToken'], selectAllAssets ? selectAllAssets.value : null]);

    const handlePageChange = (event, value) => {
        $currentPage.set(value);
    };

    const [disabledButton, setIsDisabled] = useState({ disabled: false, externalType: '' });

    return <>
        <WorkflowTitle>
            Step 4: Decide what additional actions to take on your digitized assets - {job.importJobId}
        </WorkflowTitle>
        <WorkflowBody>
            <Columns>
                <WorkflowColumnData>
                    {/* this is the asset type selector for multi asset */}
                    {$assetTypeSelect && <DigiSelectContainer><DigitizationSelect placeholder={'Asset Type'} $value={$assetTypeSelect} options={assetTypeMultiOptions} isMulti={false} /></DigiSelectContainer>}
                    {/* this is the asset filter box */}
                    <SearchInputContainer>
                        <SearchInput $value={$filter.$.search} />
                    </SearchInputContainer>
                    {/* this is the asset list view */}
                    {isReadyPagination ? assetsListComponent(filteredAssetChanges, assetChanges.count, selectAllAssets, assetEventsGeneralCount) : <Loading />}
                    <PaginationBlock size="large" count={Math.ceil(assetChanges.count / 100)} page={$currentPage.value} onChange={handlePageChange} />
                </WorkflowColumnData>
                <ActionsColumn>
                    {
                        isReadyPagination && pollAssetCountAndDisableSendButton &&
                        <Alert severity="warning">Buttons will be enabled when data processing completes.</Alert>
                    }
                    {isReadyPagination ?
                        externalSystems
                            .filter(
                                es => {
                                    const hasPermission = !es.requiredPermission || Can(es.requiredPermission);
                                    if (es.externalSystemType === GenericExternalSystemType.pts) {
                                        return hasPermission && platformProducts.items.models.some((item) => item.code === 'PTS' && item.sendToProductEnabled === true);;
                                    }
                                    if (es.externalSystemType === GenericExternalSystemType.inblock && (assetName === "Remittance Advice" || assetName === "Payment")) {
                                        return hasPermission && platformProducts.items.models.some((item) => item.code === 'INMATCH');;
                                    }
                                    return hasPermission;
                                })
                            .map(externalSystem =>
                                <>
                                    <ActionBlockButton style={{ width: $width.value + "px", height: externalSystem.externalSystemType === GenericExternalSystemType.scf || externalSystem.externalSystemType === GenericExternalSystemType.abf ? '115px' : '80px' }}
                                        qa-id={externalSystem.title}
                                        key={externalSystem.title}
                                        disabled={disableButtonsFlag || (disabledButton.disabled && disabledButton.externalType === externalSystem.type) || pollAssetCountAndDisableSendButton}
                                        onClick={() => {
                                            setIsDisabled({ disabled: true, externalType: externalSystem.type });
                                            setTimeout(() => { setIsDisabled({ disabled: false, externalType: externalSystem.type }); }, 5000);
                                            $sendToExternalSystem.set({
                                                systemType: externalSystem.type,
                                                assetType: assetName,
                                                assetEventType: externalSystem.assetEventType,
                                                importJobId: selectAllAssets.value ? job.importJobId : null
                                            })
                                        }}>
                                        <Columns>
                                            <Column>
                                                <ActionBlockTitle>{externalSystem.title}</ActionBlockTitle>
                                                <ActionBlockDescription>{externalSystem.description}</ActionBlockDescription>
                                            </Column>
                                            <ActionBlockIconColumn>
                                                <ActionBlockIcon src={externalSystem.icon} alt={externalSystem.title} />
                                            </ActionBlockIconColumn>
                                        </Columns>
                                    </ActionBlockButton>
                                    {Can(UserEntitlementCatalogs.ManageTargetProgram) && externalSystem.externalSystemType === GenericExternalSystemType.abf
                                        && !disableButtonsFlag
                                        && !pollAssetCountAndDisableSendButton
                                        && !(disabledButton.disabled && disabledButton.externalType === externalSystem.type) &&
                                        <PickerContainer style={{ width: $width.value - 2 + "px" }}>
                                            <PickerLabel>Program: </PickerLabel>
                                            <DropdownContainer>
                                                <MultiSelectDropdown
                                                    styles={{ ...customTargetProgramStyles }}
                                                    getItems={getProgramItems}
                                                    selectedItems={selectedTargetProgram}
                                                    handleChange={(selectedItem) => { setSelectedTargetProgram(selectedItem) }}
                                                />
                                            </DropdownContainer>
                                        </PickerContainer>
                                    }
                                    {Can(UserEntitlementCatalogs.ManageTargetFacility) && externalSystem.externalSystemType === GenericExternalSystemType.scf
                                        && !disableButtonsFlag
                                        && !pollAssetCountAndDisableSendButton
                                        && !(disabledButton.disabled && disabledButton.externalType === externalSystem.type) &&
                                        <PickerContainer style={{ width: $width.value - 2 + "px" }}>
                                            <PickerLabel>Facilities: </PickerLabel>
                                            <DropdownContainer>
                                                <MultiSelectDropdown
                                                    styles={{ ...customFacilityStyles }}
                                                    isMultiple={true}
                                                    getItems={getItems}
                                                    selectedItems={selectedFacilities}
                                                    handleChange={(selectedItem) => { setSelectedFacilities(selectedItem) }}
                                                />
                                            </DropdownContainer>
                                        </PickerContainer>
                                    }
                                </>
                            )
                        : <Loading />
                    }
                </ActionsColumn>
            </Columns>
        </WorkflowBody>
        <WorkflowFooter>
            <Columns>
                <StyledColumn>
                    <Flex row>
                        <BackButton color="primary" onClick={back}>Back</BackButton>
                    </Flex>
                </StyledColumn>
            </Columns>
        </WorkflowFooter>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showErrors.value} autoHideDuration={6000} onClose={handleCloseShowErrors}>
            <Alert severity={'error'}>
                <AlertTitle>Error</AlertTitle>
                {$errorMessage.value}
            </Alert>
        </Snackbar>
    </>
};

const customFacilityStyles = {
    float: "left",
    backgroundColor: 'white',
    width: '308px',
    marginLeft: '17px'
};

const customTargetProgramStyles = {
    float: "left",
    backgroundColor: 'white',
    width: '308px',
    marginLeft: '10px'
};

const PickerContainer = div({
    borderRadius: '8px',
    padding: '0px',
    marginLeft: '9px',
    backgroundColor: "#4297D6",
    zIndex: 1,
    marginTop: "-55px",
    marginBottom: "15px",
    paddingBottom: "5px",
})

const PickerLabel = div({
    float: 'left',
    paddingTop: '5px',
    marginLeft: '31px',
    marginTop: '8px',
    fontSize: "9pt",
    color: "white"
});

const StyledColumn = styled(Column)({
    flex: '1 1 auto'
});

export const ActionsColumn = styled(WorkflowColumn)({
    maxWidth: '500px'
});

export const ActionsContainer = div({
    display: 'flex',
    marginBottom: '20px',
    justifyContent: 'center',
    alignItems: 'center'
});

const DropdownContainer = div({
    margin: '0 -6px 20px 10px',
    '& .MuiAccordionSummary-content': {
        marginLeft: '13px'
    }
})

const ActionBlockButton = styled(Button)({
    cursor: 'pointer',
    background: '#4297D6',
    border: '1px solid #F4F7F9',
    boxSizing: 'border-box',
    borderRadius: '8px',
    width: '100%',
    textAlign: 'left',
    alignItems: 'start',
    paddingTop: '12px',
    marginTop: '3px',
    '&:hover': {
        backgroundColor: '#4297D6',
        fontSize: '14px !important',
        fontWeight: 600,
        color: 'white !important',
    },
    '&:active': {
        backgroundColor: '#4297D6',
        color: '#58C1EE',
        fontSize: '14px !important',
        fontWeight: 600,
    },
    '&:focus': {
        backgroundColor: '#4297D6',
        fontSize: '14px !important',
        fontWeight: 600,
    },
    '&:disabled': {
        backgroundColor: 'rgba(102,138,165,0.25)',
        opacity: 0.75,
        color: '#FFF'
    }
});

const ActionBlockTitle = div({
    fontFamily: 'Montserrat',
    fontWeight: 600,
    fontSize: '11pt',
    lineHeight: '24px',
    textTransform: 'capitalize',
    color: '#F4F7F9'

});

const ActionBlockDescription = div({
    fontFamily: 'Montserrat',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '8pt',
    paddingTop: '2px',
    lineHeight: '12px',
    color: '#F4F7F9',
});

const ActionBlockIconColumn = styled(Column)({
    maxWidth: '40px',
    justifyContent: 'center'
});

const ActionBlockIcon = ({ src, alt = '' }) => {
    const classes = useActionBlockIconStyles();
    return <img className={classes.root} src={src} alt={alt} />
}

const useActionBlockIconStyles = makeStyles({
    root: {
        width: '32px',
        height: '32px'
    }
});

const SearchInputContainer = div({
    backgroundColor: 'white !important',
    marginBottom: '3px',
    paddingLeft: '3px',
    borderRadius: '5px'
});

const DigiSelectContainer = div({
    marginBottom: '3px',
    '& div': {
        '& [class*="-control"]': {
            borderRadius: '5px'
        }
    }
});

const PaginationBlock = styled(Pagination)({
    marginTop: '5px',
    display: 'flex',
    justifyContent: 'center'
});

const WorkflowColumnData = styled(WorkflowColumn)({
    height: '600px'
});


